import {useEffect, useCallback} from 'react';
import notifier from '@albert-io/notifier';

/**
 * Get message for MediaError code
 */
function getMediaErrorMessage(code: number): string {
  switch (code) {
    case MediaError.MEDIA_ERR_ABORTED:
      return 'Audio playback aborted';
    case MediaError.MEDIA_ERR_NETWORK:
      return 'Network error loading audio';
    case MediaError.MEDIA_ERR_DECODE:
      return 'Audio decoding failed';
    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      return 'Audio format not supported';
    default:
      return 'Unknown audio error';
  }
}

/**
 * Report audio errors to Honeybadger with debugging context
 */
function reportAudioError(
  error: Error | string,
  audio: HTMLAudioElement,
  src: string,
  type: string
) {
  const errorMessage = typeof error === 'string' ? error : error.message;

  notifier.notify(new Error(errorMessage), {
    tags: 'AudioPlayer',
    context: {
      errorType: type,
      audioSrc: src,
      userAgent: navigator.userAgent,
      // Audio element state
      readyState: audio.readyState,
      networkState: audio.networkState,
      paused: audio.paused,
      currentTime: audio.currentTime,
      duration: audio.duration,
      // Browser capabilities
      canPlayMP3: audio.canPlayType('audio/mpeg'),
      canPlayOGG: audio.canPlayType('audio/ogg'),
      // MediaError details (if available)
      mediaErrorCode: audio.error?.code,
      mediaErrorMessage: audio.error?.message
    }
  });
}

export const useAudioEvents = (audioRef, src, updatePlayerState) => {
  const handleEnded = useCallback(() => {
    updatePlayerState(src, {isPlaying: false, timestamp: 0});
    audioRef!.current.currentTime = 0;
  }, [src, updatePlayerState]);

  const handleLoadedMetadata = useCallback(() => {
    updatePlayerState(src, {duration: audioRef!.current.duration});
  }, [src, updatePlayerState]);

  const updateTime = useCallback(() => {
    updatePlayerState(src, {timestamp: audioRef!.current.currentTime});
  }, [src, updatePlayerState]);

  const handleError = useCallback(() => {
    const audio = audioRef.current;
    if (!audio?.error) return;

    const errorMessage = getMediaErrorMessage(audio.error.code);
    reportAudioError(errorMessage, audio, src, 'MediaError');

    updatePlayerState(src, {isPlaying: false});
  }, [src, updatePlayerState, audioRef]);

  const addAudioEventListeners = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('timeupdate', updateTime);
      audio.addEventListener('error', handleError);
    }
  }, [handleEnded, handleLoadedMetadata, updateTime, handleError, audioRef]);

  const removeAudioEventListeners = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('error', handleError);
    }
  }, [handleEnded, handleLoadedMetadata, updateTime, handleError, audioRef]);

  useEffect(() => {
    addAudioEventListeners();
    return () => {
      removeAudioEventListeners();
    };
  }, [addAudioEventListeners, removeAudioEventListeners]);
};

export const useTrackEvents = (trackRef) => {
  const updateTrackBackground = useCallback(
    (e) => {
      const track = trackRef!.current;
      const rect = track.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const totalWidth = rect.width;
      const percentage = (x / totalWidth) * 100;
      track.style.setProperty('--hover-percentage', `${percentage}%`);
    },
    [trackRef]
  );

  const clearTrackBackground = useCallback(() => {
    trackRef!.current.style.setProperty('--hover-percentage', '0%');
  }, [trackRef]);

  const addTrackEventListeners = useCallback(() => {
    const track = trackRef.current;
    if (track) {
      track.addEventListener('mousemove', updateTrackBackground);
      track.addEventListener('mouseleave', clearTrackBackground);
    }
  }, [updateTrackBackground, clearTrackBackground, trackRef]);

  const removeTrackEventListeners = useCallback(() => {
    const track = trackRef.current;
    if (track) {
      track.removeEventListener('mousemove', updateTrackBackground);
      track.removeEventListener('mouseleave', clearTrackBackground);
    }
  }, [updateTrackBackground, clearTrackBackground, trackRef]);

  useEffect(() => {
    addTrackEventListeners();
    return () => {
      removeTrackEventListeners();
    };
  }, []);
};
